import React from 'react';
import { Calendar, MapPin, Pencil, Phone, Shield, User } from 'lucide-react';
import type {
  PatientInsuranceResponse,
  PatientResponse,
} from '../../services/api/types.gen';
import { formatDateToMMDDYYYY, calculateAge } from '../../utils/dateUtils';
import { TestPatientBadge } from './TestPatientBadge';

interface PatientHeaderProps {
  patient: PatientResponse;
  insurances?: PatientInsuranceResponse[];
  episodePatientInsuranceId?: string | null;
  onEditEpisodeInsurance?: () => void;
  /** Permission-gated controls (e.g. the test-status toggle) shown top-right. */
  actions?: React.ReactNode;
  children?: React.ReactNode;
}

const formatDate = (dateString: string | null | undefined) =>
  formatDateToMMDDYYYY(dateString) || 'N/A';

const getAge = (dateOfBirth: string) => {
  const age = calculateAge(dateOfBirth);
  return age !== null ? age : 'Unknown';
};

export const PatientHeader: React.FC<PatientHeaderProps> = ({
  patient,
  insurances,
  episodePatientInsuranceId,
  onEditEpisodeInsurance,
  actions,
  children,
}) => {
  const episodeInsurance = episodePatientInsuranceId
    ? insurances?.find(ins => ins.id === episodePatientInsuranceId)
    : undefined;
  const insuranceLabel = episodeInsurance
    ? episodeInsurance.insurance_name
    : (patient.insurance_name ?? null);
  const hasMultiple = (insurances?.length ?? 0) > 1;
  return (
    <div className='border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/85 flex-shrink-0 shadow-sm'>
      <div className='px-6 py-3'>
        <div className='flex items-start justify-between gap-4'>
          <div className='flex-1 min-w-0'>
            <div className='flex items-center gap-3 mb-1'>
              <h1 className='text-[22px] font-semibold tracking-tight text-foreground'>
                {patient.first_name} {patient.last_name}
              </h1>
              {patient.mrn && (
                <span className='text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded'>
                  MRN: {patient.mrn}
                </span>
              )}
              {patient.is_test_patient && <TestPatientBadge />}
            </div>
            <div className='text-[13px] text-muted-foreground/90 flex flex-wrap items-center gap-x-1.5 gap-y-0.5'>
              <span className='inline-flex items-center gap-1'>
                <Calendar className='h-3.5 w-3.5' />
                DOB: {formatDate(patient.date_of_birth)}
                {patient.date_of_birth && (
                  <span className='text-xs'>
                    ({getAge(patient.date_of_birth)} yrs)
                  </span>
                )}
              </span>
              <span className='text-border'>•</span>
              <span className='inline-flex items-center gap-1'>
                <Phone className='h-3.5 w-3.5' />
                {patient.phone || 'N/A'}
              </span>
              <span className='text-border'>•</span>
              <span className='inline-flex items-center gap-1'>
                <MapPin className='h-3.5 w-3.5' />
                {patient.address || 'N/A'}
                {(patient.city || patient.state || patient.zip_code) && ', '}
                {[
                  patient.city,
                  [patient.state, patient.zip_code].filter(Boolean).join(' '),
                ]
                  .filter(Boolean)
                  .join(', ')}
              </span>
              <span className='text-border'>•</span>
              <span className='inline-flex items-center gap-1'>
                <Shield className='h-3.5 w-3.5' />
                {insuranceLabel ?? 'No insurance'}
                {hasMultiple && (
                  <span className='text-xs text-muted-foreground/70'>
                    +{(insurances?.length ?? 1) - 1} more
                  </span>
                )}
                {onEditEpisodeInsurance && (
                  <button
                    type='button'
                    onClick={onEditEpisodeInsurance}
                    className='ml-0.5 p-0.5 rounded text-muted-foreground/60 hover:text-foreground hover:bg-muted transition-colors'
                    aria-label='Edit episode insurance'
                  >
                    <Pencil className='h-3 w-3' />
                  </button>
                )}
              </span>
              <span className='text-border'>•</span>
              <span className='inline-flex items-center gap-1'>
                <User className='h-3.5 w-3.5' />
                {patient.clinic_name || 'No clinic'}
              </span>
            </div>
          </div>
          {actions && <div className='flex-shrink-0'>{actions}</div>}
        </div>
      </div>
      {children}
    </div>
  );
};
